import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { isObject, pickBy } from 'lodash';
import { Observable, of } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import { UPLOAD_COMPLETE_EVENT } from 'src/app/core/dictionaries/socket-events';
import { UploadRequest, UploadResponse } from 'src/app/core/models/upload-params';
import { ConfigService } from 'src/app/core/services/config.service';
import { UploadService } from 'src/app/core/services/upload.service';
import { WebSocketService } from 'src/app/core/services/web-socket.service';
import { CoreState } from 'src/app/core/store/reducers';

@Injectable({
  providedIn: 'root',
})
export class BackendService {
  constructor(
    protected http: HttpClient,
    protected config: ConfigService,
    protected store$: Store<CoreState>,
    protected webSocket: WebSocketService,
    protected uploadService: UploadService,
  ) {}

  public get<R = any>(
    url: string,
    params?: any,
    raw = false,
    responseType = 'json',
    returnResponse = false,
  ): Observable<R> {
    return this.getHeaders().pipe(
      mergeMap((headers) => {
        const options: any = {
          params: isObject(params) ? JSON.parse(JSON.stringify(params)) : params, // remove undefined
          headers,
          responseType,
        };

        if (returnResponse) {
          options.observe = 'response';
        }

        return this.http.get(this.getRootUrl(url), options);
      }),
      map((res) => (raw ? res : this.getResponsePayload(res))),
    );
  }

  public post<R = any>(url: string, data?: any, raw = false): Observable<R> {
    return this.getHeaders().pipe(
      mergeMap((headers) => {
        return this.http.post(this.getRootUrl(url), data, { headers });
      }),
      map((res) => (raw ? res : this.getResponsePayload(res))),
    );
  }

  public put<R = any>(url: string, data?: any, raw = false): Observable<R> {
    return this.getHeaders().pipe(
      mergeMap((headers) => {
        return this.http.put(this.getRootUrl(url), data, { headers });
      }),
      map((res) => (raw ? res : this.getResponsePayload(res))),
    );
  }

  public delete<R = any>(url: string, params?: any, raw = false): Observable<R> {
    return this.getHeaders().pipe(
      mergeMap((headers) => {
        return this.http.delete(this.getRootUrl(url), { params: pickBy(params, (v) => v !== void 0), headers });
      }),
      map((res) => (raw ? res : this.getResponsePayload(res))),
    );
  }

  public upload(req: UploadRequest): Observable<UploadResponse> {
    const { file, portalId, guid, url = 'file/upload', socket = true } = req;
    return this.getHeaders().pipe(
      mergeMap((headers) => {
        return this.uploadService
          .upload({
            headers,
            url: this.getRootUrl(url),
            files: file,
            data: {
              productId: portalId,
              guids: guid ? [guid] : void 0,
            },
          })
          .pipe(
            map((res) => res.data),
            mergeMap((data: any) => {
              const { id: fileId, url } = data;
              if (!socket) return of({ fileId, url });

              return this.webSocket.message$.pipe(
                filter((msg) => msg.event === UPLOAD_COMPLETE_EVENT && msg.data.fileId === fileId),
                take(1),
                map((msg) => ({ fileId, url: msg.data.fileHref })),
              );
            }),
          );
      }),
    );
  }

  protected getHeaders() {
    return of(
      new HttpHeaders({
        [this.config.env.domainHeader]: this.config.hostname,
        [this.config.env.clientIdHeader]: this.config.clientId,
      }),
    );
  }

  protected getRootUrl(path: string) {
    return `${this.config.env.apiRoot}/${path}`;
  }

  protected getResponsePayload(responce: any) {
    return responce ? responce.data : void 0;
  }
}
